import { SensorType } from '../../sensors/types/sensor-type';
import { IntegrationType } from '../../types/integration-type';

export enum SensorStatus {
  ACTIVE = 0,
  OFFLINE = 1,
  DISABLED = 2,
  NOT_CONFIGURED = 3,
  ACTION_NEEDED = 4,
  BAD_AUDIT_POLICY_CONFIG = 5,
  BEST_NOT_INSTALLED = 6,
  RESTART_NEEDED = 7,
  NOT_LICENSED = 8,
  BAD_CERTIFICATE_AUDIT_POLICY_CONFIG = 9,
  UNKNOWN = 99,
}

export const NSVA_SENSOR_STATUS_OFFLINE = 1;

export enum SensorResponseStatus {
  NOT_AVAILABLE = -1,
  ACTIVE = 1,
  ERROR = 2,
  UNKNOWN = 0,
  DISABLED = 3,
}

export enum SensorEnabledStatus {
  DISABLED = 0,
  ENABLED = 1,
  NOT_CONFIGURED = 3,
}

export interface Error {
  error: string;
  error_description: string;
  error_codes: string | null;
  timestamp: string;
  trace_id: string;
  correlation_id: string;
  error_uri: string;
}

export interface Sensor {
  _id: string;
  type: SensorType;
  name: string | null;
  status: SensorStatus;
  responseActionStatus: SensorResponseStatus;
  responseActionErrorMessage?: string|null;
  lastError: Error | null;
  lastErrorStatus: number;
  specifics: {
    vulnerabilityScanner?: VulnerabilityScanner;
    lastReportedDate?: string | null;
    monitoredNetwork?: string | null;
    subnet?: string | null;
    interface?: string | null;
    subscriptionId?: string;
    domain?: string;
    gateways?: Gateway[];
    subnets?: Subnet[];
    groupId?: number;
    mgmtInterface?: MgmtInterface;
    version?: number;
    orgId?: string;
    responseActions?: boolean;
    roles?: string[];
    cloud?: {
      platform: string;
      data?: NSVACloudAzureData;
    }
  };
  networkSensorStatus?: number;
  vulnerabilityScannerStatus?: number;
}

export interface Gateway {
  ip: string;
  mac: string;
}

export interface Subnet {
  subnet: string;
  isTrafficTagged: IsTrafficTagged;
  vlanId?: number;
}

interface MgmtInterface {
  hostname?: string;
  ip?: string;
  communicationInterface?: string;
  dhcpEnabled?: boolean;
}

export interface NSVAScanInterface {
  ip: string;
  mac: string;
  name: string;
}

export interface NSVAScanTarget {
  subnet: string;
}

interface VulnerabilityScanner {
  enabled?: boolean;
  scanInterfaces?: NSVAScanInterface[];
  scanTargets?: NSVAScanTarget[];
  isFunctioning?: boolean;
  reason?: string;
}

export enum IsTrafficTagged {
  YES = 1,
  NO = 0,
  UNKNOWN = -1,
}

export const NSVA_DEFAULT_VERSION = 1;

export enum MetadataStatus {
  PENDING = 0,
  DELIVERED = 1,
  DISMISSED = 2,
  DELIVERED_WITH_ERROR = 3,
  NOT_DELIVERED = 4,
  FAILED_SESSION_START = 5,
}

export enum IntegrationStatus {
  ACTIVE = 0,
  ACTION_NEEDED = 1,
  DISABLED = 2,
  NOT_LICENSED = 3,
  ACTION_NEEDED_YELLOW = 4,
}

export enum IntegrationResponseStatus {
  ACTIVE = 1,
  UNKNOWN = 0,
  NOT_AVAILABLE = -1,
  DISABLED = 2,
}

export interface Integration {
  _id: string;
  name: string;
  type: IntegrationType;
  status: IntegrationStatus;
  responseActionStatus: IntegrationResponseStatus;
  hasIntegrationFeature: boolean;
  companyId: string;
  companyName: string;
  nonUserConfigurable: boolean;
  sensors: Sensor[];
}

export enum IntegrationEvents {
  SENSOR_SET_ENABLED_STATUS = 'sensor_set_enabled_status',
  INTEGRATION_SET_ENABLED_STATUS = 'integration_set_enabled_status',
  INTEGRATION_DELETE = 'integration_delete',
  INTEGRATION_EDIT = 'integration_edit',
  DELETE_SENSOR = 'delete_sensor',
}

export interface ConnectivityError {
  errorCode: number;
  errorUri: string;
}

export interface NSVACloudAzureData {
  location: string;
  name: string;
  resourceGroupName: string;
  subscriptionId: string;
  vmId: string;
  vmSize: string;
}

export enum NSVACloudPlatform {
  AZURE = 'Azure'
}
